import React, { useState, useEffect } from 'react';
import {
    Plus,
    Search,
    Shield,
    Mail,
    User as UserIcon,
    Trash2,
    Lock,
} from 'lucide-react';
import { adminApiClient, User } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import DataTable from './common/DataTable';
import Swal from 'sweetalert2';
import PermissionSelector, { DEFAULT_ADMIN_PERMISSIONS } from './common/PermissionSelector';
import { showDeleteConfirmation } from '../lib/sweetAlert';

const AdminManagementPage: React.FC = () => {
    const { user: currentUser } = useAuth();
    const [admins, setAdmins] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'admin' as 'admin' | 'super_admin',
        permissions: [] as string[]
    });
    const [submitting, setSubmitting] = useState(false);

    const fetchAdmins = async () => {
        try {
            setLoading(true);

            const [resAdmins, resSuper] = await Promise.all([
                adminApiClient.getUsers({ role: 'admin', limit: 100 }),
                adminApiClient.getUsers({ role: 'super_admin', limit: 100 })
            ]);

            let allAdmins: User[] = [];
            if (resAdmins.success && resAdmins.data) allAdmins = [...allAdmins, ...resAdmins.data.users];
            if (resSuper.success && resSuper.data) allAdmins = [...allAdmins, ...resSuper.data.users];

            // Remove duplicates just in case
            const uniqueAdmins = Array.from(new Map(allAdmins.map(item => [(item as any)._id || item.id, item])).values());

            setAdmins(uniqueAdmins);
        } catch (error) {
            console.error('Failed to fetch admins:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load admin list',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdmins();
    }, []);

    const handleCreateAdmin = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const response = await adminApiClient.createUser({
                name: formData.name,
                email: formData.email,
                password: formData.password,
                role: formData.role,
                permissions: formData.permissions
            });

            if (response.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Admin created successfully',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000
                });
                setShowAddModal(false);
                setFormData({ name: '', email: '', password: '', role: 'admin', permissions: [] });
                fetchAdmins();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.message || 'Failed to create admin',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000
                });
            }
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'An error occurred',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteAdmin = async (id: string) => {
        const isConfirmed = await showDeleteConfirmation('Delete Admin', 'Are you sure you want to permanently delete this admin?');

        if (!isConfirmed) return;

        try {
            const response = await adminApiClient.deleteUser(id);
            if (response.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Admin has been permanently deleted.',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000
                });
                fetchAdmins();
            } else {
                Swal.fire('Error', response.message || 'Failed to delete', 'error');
            }
        } catch (error: any) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    // When role changes, set default permissions
    useEffect(() => {
        if (showAddModal && formData.role === 'admin' && formData.permissions.length === 0) {
            setFormData(prev => ({ ...prev, permissions: DEFAULT_ADMIN_PERMISSIONS }));
        } else if (showAddModal && formData.role === 'super_admin') {
            setFormData(prev => ({ ...prev, permissions: [] })); // Super Admin has all by default
        }
    }, [formData.role, formData.permissions.length, showAddModal]);

    const columns = [
        {
            header: 'Admin',
            accessorKey: 'name',
            cell: (info: any) => (
                <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold mr-3">
                        {info.getValue().charAt(0)}
                    </div>
                    <div>
                        <div className="font-medium text-gray-900">{info.getValue()}</div>
                        <div className="text-gray-500 text-sm">{info.row.original.email}</div>
                    </div>
                </div>
            )
        },
        {
            header: 'Role',
            accessorKey: 'role',
            cell: (info: any) => (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border
          ${info.getValue() === 'super_admin'
                        ? 'bg-purple-100 text-purple-800 border-purple-200'
                        : 'bg-blue-100 text-blue-800 border-blue-200'
                    }`}>
                    {info.getValue() === 'super_admin' ? 'Super Admin' : 'Admin'}
                </span>
            )
        },
        {
            header: 'Permissions',
            id: 'permissions',
            cell: (info: any) => {
                const role = info.row.original.role;
                const perms = info.row.original.permissions || [];

                if (role === 'super_admin') {
                    return <span className="text-xs text-gray-500 italic">All Permissions</span>;
                }
                if (!perms.length) {
                    return <span className="text-xs text-gray-400">Default</span>;
                }

                return (
                    <div className="flex flex-wrap gap-1 max-w-xs">
                        {perms.slice(0, 3).map((p: string) => (
                            <span key={p} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded border border-gray-200">
                                {p.replace(/_/g, ' ')}
                            </span>
                        ))}
                        {perms.length > 3 && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded border border-gray-200">
                                +{perms.length - 3} more
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'Status',
            accessorKey: 'isActive',
            cell: (info: any) => {
                const isActive = info.getValue() !== false;
                return (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {isActive ? 'Active' : 'Inactive'}
                    </span>
                )
            }
        },
        {
            header: 'Actions',
            id: 'actions',
            cell: (info: any) => {
                const adminId = info.row.original._id || info.row.original.id;
                const currentUserId = currentUser?._id || currentUser?.id;
                const isAdminSelf = adminId === currentUserId;
                if (isAdminSelf) return <span className="text-xs text-gray-400">Current User</span>;

                return (
                    <div className="flex justify-end gap-2">
                        <button
                            onClick={() => handleDeleteAdmin(adminId)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                            title="Deactivate Admin"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                )
            }
        }
    ];

    const filteredAdmins = admins.filter(admin =>
        admin.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        admin.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Admin Management</h1>
                    <p className="text-gray-500">Manage system administrators and their roles</p>
                </div>
                <button
                    onClick={() => {
                        setFormData({ name: '', email: '', password: '', role: 'admin', permissions: [] });
                        setShowAddModal(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                    <Plus className="w-5 h-5" />
                    Add Admin
                </button>
            </div>

            {/* Search */}
            <div className="mb-6 relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                    type="text"
                    placeholder="Search admins by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <DataTable
                    columns={columns}
                    data={filteredAdmins}
                    loading={loading}
                    pagination={undefined}
                    sorting={undefined}
                    enableClientSidePagination={true}
                />
            </div>

            {/* Create Admin Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 animate-scale-in max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <Shield className="w-6 h-6 text-indigo-600" />
                            Add New Admin
                        </h2>

                        <form onSubmit={handleCreateAdmin} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                    <div className="relative">
                                        <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                                            placeholder="John Doe"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input
                                            type="email"
                                            required
                                            value={formData.email}
                                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                                            placeholder="admin@example.com"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="password"
                                        required
                                        minLength={6}
                                        value={formData.password}
                                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                                        placeholder="••••••••"
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                                <div className="grid grid-cols-2 gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, role: 'admin' })}
                                        className={`flex items-center justify-center px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${formData.role === 'admin'
                                            ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                                            : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                                            }`}
                                    >
                                        Admin
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, role: 'super_admin' })}
                                        className={`flex items-center justify-center px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${formData.role === 'super_admin'
                                            ? 'border-purple-600 bg-purple-50 text-purple-700'
                                            : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                                            }`}
                                    >
                                        Super Admin
                                    </button>
                                </div>
                            </div>

                            {formData.role === 'admin' && (
                                <div className="pt-2 border-t border-gray-100">
                                    <PermissionSelector
                                        selectedPermissions={formData.permissions}
                                        onChange={(perms) => setFormData({ ...formData, permissions: perms })}
                                    />
                                </div>
                            )}

                            {formData.role === 'super_admin' && (
                                <div className="p-4 bg-purple-50 rounded-lg border border-purple-100 text-purple-700 text-sm flex items-center gap-2">
                                    <Shield className="w-5 h-5" />
                                    Super Admin has full access to all system features.
                                </div>
                            )}

                            <div className="flex gap-3 mt-6 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50"
                                    style={{ opacity: submitting ? 0.7 : 1 }}
                                >
                                    {submitting ? 'Creating...' : 'Create Admin'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminManagementPage;
